import {
	CloudUpload,
	CheckCircle,
	AttachFile,
	TableChartOutlined,
	ImageOutlined,
} from "@mui/icons-material";
import { Button, Chip, Paper, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { GlobalState } from "context/GlobalContext";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import FileDetailsDialogue, { LOG_TABS } from "./FileDetailsDialogue";
import PayByLinkPreviewDialog from "./PayByLinkPreviewDialog";
import UploadCalendar from "./UploadCalendar";
import { validateMappingAgainstFile, POS_RECO_MAPPING_MOPS } from "../ColumnMapping/posRecoColumnSchema";
import { readPosRecoFileHeaders } from "../ColumnMapping/posRecoFileColumnUtils";

const DEFAULT_UPLOAD_ENDPOINT = "/cash-card-upload";
const PAY_BY_LINK_READER_ENDPOINT = "/cash-card/pos-image-reader";
const PAY_BY_LINK_APPROVE_ENDPOINT = "/cash-card/pos-image-reader/approve";
const PAY_BY_LINK_FILE_KEY = "PAY_BY_LINK";

const SPREADSHEET_ACCEPT =
	".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";
const XLSX_WORKSHEET_ACCEPT =
	".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const IMAGE_ACCEPT = "image/*,.png,.jpg,.jpeg,.webp,.gif,.bmp";

const isSpreadsheetFile = (file) => {
	const name = file.name.toLowerCase();
	return (
		name.endsWith(".csv") ||
		name.endsWith(".xlsx") ||
		name.endsWith(".xls") ||
		file.type === "text/csv" ||
		file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
		file.type === "application/vnd.ms-excel"
	);
};

const isXlsxWorksheetFile = (file) => {
	const name = file.name.toLowerCase();
	if (!name.endsWith(".xlsx")) return false;
	return (
		!file.type ||
		file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
		file.type === "application/octet-stream"
	);
};

const isImageFile = (file) =>
	file.type.startsWith("image/") || /\.(png|jpe?g|webp|gif|bmp)$/i.test(file.name);

const getPayByLinkFiles = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const COLUMN_MAPPINGS_ENDPOINT = "/api/pos_reco/column_mappings";

const UploadFieldCard = ({ column, data, setData, onInvalidFile, onFileValidated }) => {
	const isPayByLink = column.id === PAY_BY_LINK_FILE_KEY;
	const isMultiple = Boolean(column.multiple);
	const [invalidSelection, setInvalidSelection] = useState(false);

	const payByLinkFiles = isPayByLink ? getPayByLinkFiles(data[column?.id]) : [];
	const fileSelected = isPayByLink ? payByLinkFiles.length > 0 : Boolean(data[column?.id]);

	useEffect(() => {
		if (!fileSelected) setInvalidSelection(false);
	}, [fileSelected]);

	const validateFile = (file) => {
		if (isPayByLink) return isImageFile(file);
		if (column.excelOnly) return isXlsxWorksheetFile(file);
		return isSpreadsheetFile(file);
	};

	const getInvalidFileMessage = () => {
		if (isPayByLink) return "Pay By Link accepts image files only (PNG, JPG, WEBP, etc.).";
		if (column.excelOnly) return `${column.name} accepts Microsoft Excel Worksheet (.xlsx) only.`;
		return `${column.name} accepts CSV or Excel files only (.csv, .xlsx, .xls).`;
	};

	const handleFileChange = (e) => {
		const selectedFiles = Array.from(e.target.files || []);
		if (!selectedFiles.length) return;

		if (isPayByLink && isMultiple) {
			const validFiles = [];
			let hasInvalid = false;

			selectedFiles.forEach((newFile) => {
				if (!isImageFile(newFile)) {
					hasInvalid = true;
					return;
				}
				validFiles.push(new File([newFile], `${column.id}-${newFile.name}`, { type: newFile.type }));
			});

			if (hasInvalid) {
				setInvalidSelection(true);
				onInvalidFile?.("Pay By Link accepts image files only (PNG, JPG, WEBP, etc.).");
			}

			if (!validFiles.length) {
				e.target.value = "";
				return;
			}

			setInvalidSelection(false);
			setData({ ...data, [column.id]: [...getPayByLinkFiles(data[column.id]), ...validFiles] });
			e.target.value = "";
			return;
		}

		const newFile = selectedFiles[0];

		if (!validateFile(newFile)) {
			e.target.value = "";
			setInvalidSelection(true);
			onInvalidFile?.(getInvalidFileMessage());
			return;
		}

		setInvalidSelection(false);
		const modifiedFile = new File([newFile], `${column.id}-${newFile.name}`, { type: newFile.type });
		Promise.resolve(onFileValidated?.(column.id, modifiedFile)).then((isValid) => {
			if (isValid === false) {
				e.target.value = "";
				return;
			}
			setData((current) => ({ ...current, [column.id]: modifiedFile }));
			e.target.value = "";
		});
	};

	const handleRemovePayByLinkFile = (index) => {
		const nextFiles = getPayByLinkFiles(data[column.id]).filter((_, fileIndex) => fileIndex !== index);
		setData({ ...data, [column.id]: nextFiles });
	};

	const helperText = isPayByLink
		? "PNG, JPG, WEBP, GIF — one or more images"
		: column.excelOnly
			? "Microsoft Excel Worksheet (.xlsx) only"
			: "CSV, XLSX, XLS";
	const displayName = isPayByLink
		? payByLinkFiles.length
			? `${payByLinkFiles.length} image${payByLinkFiles.length > 1 ? "s" : ""} selected`
			: "Choose images..."
		: data[column?.id]
			? data[column.id].name.replace(`${column.id}-`, "")
			: "Choose file...";

	return (
		<Paper
			elevation={0}
			sx={{
				p: 2,
				borderRadius: 2,
				border: "1px solid",
				borderColor: fileSelected ? "success.light" : "divider",
				bgcolor: fileSelected ? "success.50" : "background.paper",
				display: "flex",
				flexDirection: "column",
				gap: 1.25,
				minHeight: 148,
				boxSizing: "border-box",
			}}>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
				<Typography
					variant="subtitle2"
					fontWeight={600}
					color="text.primary"
					sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
					{column?.name}
				</Typography>
				{fileSelected && <Chip label="Ready" size="small" color="success" variant="outlined" />}
			</Box>

			<Button
				component="label"
				fullWidth
				disableElevation
				disabled={column?.disabled}
				sx={{
					justifyContent: "flex-start",
					textTransform: "none",
					bgcolor: invalidSelection ? "error.50" : "grey.50",
					color: invalidSelection ? "error.dark" : fileSelected ? "success.dark" : "text.secondary",
					border: "2px dashed",
					borderColor: invalidSelection ? "error.main" : fileSelected ? "success.main" : "grey.300",
					borderRadius: 1.5,
					py: 1.5,
					px: 1.5,
					"& .MuiButton-startIcon": { mr: 1 },
				}}
				startIcon={fileSelected ? <CheckCircle fontSize="small" /> : <AttachFile fontSize="small" />}>
				<Typography
					variant="body2"
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						fontWeight: 500,
						flex: 1,
						textAlign: "left",
					}}>
					{displayName}
				</Typography>
				<input
					type="file"
					id={column?.id}
					accept={column?.accept}
					multiple={isMultiple}
					onChange={handleFileChange}
					hidden
					disabled={column?.disabled}
				/>
			</Button>

			{isPayByLink && payByLinkFiles.length > 0 && (
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
					{payByLinkFiles.map((file, index) => (
						<Chip
							key={`${file.name}-${file.lastModified}-${index}`}
							label={file.name.replace(`${column.id}-`, "")}
							size="small"
							onDelete={() => handleRemovePayByLinkFile(index)}
							sx={{ maxWidth: "100%" }}
						/>
					))}
				</Box>
			)}

			<Typography variant="caption" color={invalidSelection ? "error.main" : "text.secondary"}>
				{invalidSelection ? getInvalidFileMessage() : `Accepted: ${helperText}`}
			</Typography>
		</Paper>
	);
};

const fileTypes = {
	PHONE_PE_REPORT: {
		name: "Phone Pe",
		key: "PHONE_PE_REPORT",
		id: "PHONEPE",
		accept: SPREADSHEET_ACCEPT,
		uploadEndpoint: DEFAULT_UPLOAD_ENDPOINT,
	},
	BANK_STATEMENT: {
		name: "Bank Statement",
		key: "BANK_STATEMENT",
		id: "BANK_STATEMENT",
		accept: SPREADSHEET_ACCEPT,
		uploadEndpoint: DEFAULT_UPLOAD_ENDPOINT,
	},
	AMEX: { name: "Amex", key: "AMEX", id: "AMEX", accept: SPREADSHEET_ACCEPT, uploadEndpoint: DEFAULT_UPLOAD_ENDPOINT },
	CARD: {
		name: "Card",
		key: "CARD",
		id: "CARD",
		accept: XLSX_WORKSHEET_ACCEPT,
		excelOnly: true,
		uploadEndpoint: DEFAULT_UPLOAD_ENDPOINT,
	},
	UPI: {
		name: "UPI",
		key: "UPI",
		id: "UPI",
		accept: XLSX_WORKSHEET_ACCEPT,
		excelOnly: true,
		uploadEndpoint: DEFAULT_UPLOAD_ENDPOINT,
	},
	MAGICPIN: {
		name: "Magicpin",
		key: "MAGICPIN",
		id: "MAGICPIN",
		accept: SPREADSHEET_ACCEPT,
		uploadEndpoint: DEFAULT_UPLOAD_ENDPOINT,
	},
	ZOMATO: {
		name: "Zomato",
		key: "ZOMATO",
		id: "ZOMATO",
		accept: SPREADSHEET_ACCEPT,
		uploadEndpoint: DEFAULT_UPLOAD_ENDPOINT,
	},
	PAY_BY_LINK: {
		name: "Pay By Link",
		key: "PAY_BY_LINK",
		id: PAY_BY_LINK_FILE_KEY,
		accept: IMAGE_ACCEPT,
		uploadEndpoint: PAY_BY_LINK_READER_ENDPOINT,
		isImage: true,
		multiple: true,
	},
};

const countSelectedFiles = (fileMap) =>
	Object.entries(fileMap).reduce((count, [, value]) => {
		if (Array.isArray(value)) return count + value.length;
		return value ? count + 1 : count;
	}, 0);

const countSpreadsheetFiles = (fileMap) =>
	Object.entries(fileMap).reduce((count, [fileKey, value]) => {
		if (fileKey === PAY_BY_LINK_FILE_KEY || Array.isArray(value)) return count;
		return value ? count + 1 : count;
	}, 0);

const FileUpload = () => {
	const [files, setFiles] = useState({});
	const [columnMappings, setColumnMappings] = useState([]);
	const [columnMappingsLoaded, setColumnMappingsLoaded] = useState(false);
	const [fileDialogOpen, setFileDialogOpen] = useState(false);
	const [uploadLogsTab, setUploadLogsTab] = useState("spreadsheet");
	const [payByLinkPreviewOpen, setPayByLinkPreviewOpen] = useState(false);
	const [payByLinkPreview, setPayByLinkPreview] = useState(null);
	const [payByLinkPendingImages, setPayByLinkPendingImages] = useState([]);
	const [payByLinkApproving, setPayByLinkApproving] = useState(false);
	const [uploadCalendarKey, setUploadCalendarKey] = useState(0);
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();

	useEffect(() => {
		const loadColumnMappings = async () => {
			try {
				const { data } = await axios.get(COLUMN_MAPPINGS_ENDPOINT);
				setColumnMappings(Array.isArray(data?.result) ? data.result : []);
			} catch (error) {
				console.error("Unable to load POS Reco column mappings", error);
			} finally {
				setColumnMappingsLoaded(true);
			}
		};
		loadColumnMappings();
	}, []);

	const { spreadsheetTypes, imageTypes } = useMemo(() => {
		const entries = Object.values(fileTypes);
		return {
			spreadsheetTypes: entries.filter((type) => !type.isImage),
			imageTypes: entries.filter((type) => type.isImage),
		};
	}, []);

	const selectedCount = countSelectedFiles(files);
	const spreadsheetCount = countSpreadsheetFiles(files);
	const payByLinkImages = getPayByLinkFiles(files[PAY_BY_LINK_FILE_KEY]);

	const handleReset = () => {
		setFiles({});
		setPayByLinkPreview(null);
		setPayByLinkPendingImages([]);
		setPayByLinkPreviewOpen(false);
		Object.values(fileTypes).forEach((type) => {
			const element = document.getElementById(type.id);
			if (element) element.value = "";
		});
	};

	const clearPayByLinkSelection = () => {
		setFiles((current) => {
			const next = { ...current };
			delete next[PAY_BY_LINK_FILE_KEY];
			return next;
		});
		const element = document.getElementById(PAY_BY_LINK_FILE_KEY);
		if (element) element.value = "";
	};

	const clearSpreadsheetSelection = () => {
		setFiles((current) => {
			const next = { ...current };
			spreadsheetTypes.forEach((type) => {
				delete next[type.id];
				const element = document.getElementById(type.id);
				if (element) element.value = "";
			});
			return next;
		});
	};

	const showSnackBar = (message, severity) => {
		setSnackBarOpen(true);
		setSnackBar({
			...snackBar,
			message,
			severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		});
	};

	const showInvalidFileMessage = (message) => showSnackBar(message, "warning");

	const getMopMapping = (fileKey) =>
		columnMappings.find((mop) => mop.id === fileKey && mop.columns?.length) ||
		POS_RECO_MAPPING_MOPS.find((mop) => mop.id === fileKey) ||
		null;

	const validateFileAgainstMapping = async (fileKey, file) => {
		if (fileKey === PAY_BY_LINK_FILE_KEY) return true;
		if (!columnMappingsLoaded) {
			showSnackBar("Column mappings are still loading. Please try again in a moment.", "warning");
			return false;
		}
		const mop = getMopMapping(fileKey);
		if (!mop?.columns?.length) {
			showSnackBar(
				`${fileKey}: column mapping is not available. Open Column Mapping and save it first.`,
				"error",
			);
			return false;
		}

		try {
			const fileHeaders = await readPosRecoFileHeaders(file, mop, mop.columns);
			const validation = validateMappingAgainstFile(mop.columns, fileHeaders);
			if (!validation.ok) {
				showSnackBar(
					`${mop.name}: ${validation.errors[0] || "file headers do not match the saved mapping. Update Column Mapping."}`,
					"error",
				);
				return false;
			}
			return true;
		} catch (error) {
			showSnackBar(`${mop.name}: unable to read file headers for column mapping.`, "error");
			return false;
		}
	};

	const uploadFile = (fileKey, file) => {
		const fileTypeConfig = Object.values(fileTypes).find((type) => type.id === fileKey);
		const form = new FormData();
		form.append("file", file);
		form.append("file_type", fileKey);

		return pythonVmInstance.post(fileTypeConfig?.uploadEndpoint || DEFAULT_UPLOAD_ENDPOINT, form, {
			headers: { "Content-Type": "multipart/form-data" },
		});
	};

	const toOriginalImageFile = (file) => {
		const originalName = file.name.replace(/^PAY_BY_LINK-/, "");
		if (originalName === file.name) return file;
		return new File([file], originalName, { type: file.type });
	};

	const buildPayByLinkReaderFormData = (imageFiles) => {
		const form = new FormData();
		imageFiles.forEach((file) => form.append("file", toOriginalImageFile(file)));
		return form;
	};

	const getPayByLinkApproveRows = (previewResponse) => {
		const resultPayload = previewResponse?.result ?? previewResponse;
		return Array.isArray(resultPayload?.data) ? resultPayload.data : [];
	};

	const buildPayByLinkApproveFormData = (imageFiles, rows) => {
		const form = new FormData();
		imageFiles.forEach((file) => form.append("file", toOriginalImageFile(file)));
		form.append("rows", JSON.stringify(rows));
		return form;
	};

	const uploadPayByLinkPreview = async (imageFiles) => {
		const response = await pythonVmInstance.post(PAY_BY_LINK_READER_ENDPOINT, buildPayByLinkReaderFormData(imageFiles));
		return response.data;
	};

	const handlePayByLinkApprove = async (selectedRows = []) => {
		if (payByLinkPreview == null || !payByLinkPendingImages.length) return;

		const rows = Array.isArray(selectedRows) && selectedRows.length
			? selectedRows
			: getPayByLinkApproveRows(payByLinkPreview);

		if (!rows.length) {
			showInvalidFileMessage("Select at least one row to approve.");
			return;
		}

		setPayByLinkApproving(true);
		try {
			await pythonVmInstance.post(
				PAY_BY_LINK_APPROVE_ENDPOINT,
				buildPayByLinkApproveFormData(payByLinkPendingImages, rows),
				{ headers: { "Content-Type": "multipart/form-data" } }
			);
			showSnackBar(
				`${rows.length} Pay By Link row${rows.length > 1 ? "s" : ""} approved and saved successfully.`,
				"success"
			);
			setPayByLinkPreviewOpen(false);
			setPayByLinkPreview(null);
			setPayByLinkPendingImages([]);
			clearPayByLinkSelection();
		} catch (error) {
			const message = error?.response?.data?.message || "Unable to approve Pay By Link upload.";
			showSnackBar(message, "error");
		} finally {
			setPayByLinkApproving(false);
		}
	};

	const getSpreadsheetEntries = () =>
		Object.entries(files).filter(([fileKey]) => fileKey !== PAY_BY_LINK_FILE_KEY);

	const validateSpreadsheetUpload = () => {
		const spreadsheetEntries = getSpreadsheetEntries();

		if (!spreadsheetEntries.length) {
			showInvalidFileMessage("Please select at least one spreadsheet file.");
			return false;
		}

		const invalidSpreadsheet = spreadsheetEntries.find(([fileKey, file]) => {
			if (fileKey === "UPI" || fileKey === "CARD") return !isXlsxWorksheetFile(file);
			return !isSpreadsheetFile(file);
		});
		if (invalidSpreadsheet) {
			const [fileKey] = invalidSpreadsheet;
			showInvalidFileMessage(
				fileKey === "UPI" || fileKey === "CARD"
					? "UPI and Card accept Microsoft Excel Worksheet (.xlsx) only."
					: "Spreadsheet uploads accept CSV or Excel files only."
			);
			return false;
		}

		return true;
	};

	const handleSpreadsheetUpload = async () => {
		if (!validateSpreadsheetUpload()) return;

		const spreadsheetEntries = getSpreadsheetEntries();
		setLoadingOpen(true);

		try {
			for (const [fileKey, file] of spreadsheetEntries) {
				const isValid = await validateFileAgainstMapping(fileKey, file);
				if (!isValid) {
					setLoadingOpen(false);
					return;
				}
			}

			await Promise.all(spreadsheetEntries.map(([fileKey, file]) => uploadFile(fileKey, file)));
			showSnackBar("Spreadsheet files uploaded successfully. Jobs are being created.", "success");
			clearSpreadsheetSelection();
			setUploadCalendarKey((prev) => prev + 1);
		} catch (error) {
			const message = error?.response?.data?.message || "Something went wrong while creating jobs.";
			showSnackBar(message, "error");
		} finally {
			setLoadingOpen(false);
		}
	};

	const handlePayByLinkProcess = async () => {
		if (!payByLinkImages.length) {
			showInvalidFileMessage("Please select at least one Pay By Link image.");
			return;
		}

		const invalidPayByLinkImage = payByLinkImages.find((file) => !isImageFile(file));
		if (invalidPayByLinkImage) {
			showInvalidFileMessage("Pay By Link accepts image files only.");
			return;
		}

		setLoadingOpen(true);

		try {
			const preview = await uploadPayByLinkPreview(payByLinkImages);
			setPayByLinkPendingImages(payByLinkImages);
			setPayByLinkPreview(preview);
			setPayByLinkPreviewOpen(true);
		} catch (error) {
			const message = error?.response?.data?.message || "Something went wrong while processing Pay By Link images.";
			showSnackBar(message, "error");
		} finally {
			setLoadingOpen(false);
		}
	};

	const renderSection = (title, subtitle, icon, types, action) => (
		<Paper
			elevation={0}
			sx={{
				p: { xs: 2, md: 2.5 },
				mb: 3,
				borderRadius: 2,
				border: "1px solid",
				borderColor: "divider",
				bgcolor: "grey.50",
			}}>
			<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2.5 }}>
				<Box
					sx={{
						mt: 0.25,
						width: 32,
						height: 32,
						borderRadius: 1.5,
						bgcolor: "background.paper",
						border: "1px solid",
						borderColor: "divider",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}>
					{icon}
				</Box>
				<Box>
					<Typography variant="subtitle1" fontWeight={700}>
						{title}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
						{subtitle}
					</Typography>
				</Box>
			</Box>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: {
						xs: "1fr",
						sm: "repeat(2, minmax(0, 1fr))",
						lg: "repeat(3, minmax(0, 1fr))",
					},
					gap: 2.5,
					alignItems: "start",
				}}>
				{types.map((type) => (
					<UploadFieldCard
						key={type.id}
						data={files}
						setData={setFiles}
						column={type}
						onInvalidFile={showInvalidFileMessage}
						onFileValidated={validateFileAgainstMapping}
					/>
				))}
			</Box>

			{action && (
				<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
					<Button
						variant="contained"
						disableElevation
						onClick={action.onClick}
						disabled={action.disabled}
						startIcon={action.icon}
						sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3 }}>
						{action.label}
					</Button>
				</Box>
			)}
		</Paper>
	);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				minHeight: 0,
				boxSizing: "border-box",
				p: { xs: 1.5, sm: 2 },
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}>
			<Paper
				elevation={0}
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					borderRadius: 2.5,
					border: "1px solid",
					borderColor: "divider",
					bgcolor: "background.paper",
					overflow: "hidden",
				}}>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflowY: "auto",
						overflowX: "hidden",
						px: { xs: 2.5, md: 3 },
						py: 3,
					}}>
					{selectedCount > 0 && (
						<Box sx={{ mb: 2 }}>
							<Chip
								label={`${selectedCount} file${selectedCount > 1 ? "s" : ""} selected`}
								size="small"
								color="primary"
								variant="outlined"
							/>
						</Box>
					)}
					<UploadCalendar refreshKey={uploadCalendarKey} />
					{renderSection(
						"Spreadsheet reports",
						"CSV or Excel files for payment and settlement data",
						<TableChartOutlined fontSize="small" color="primary" />,
						spreadsheetTypes,
						{
							label: "Upload files",
							onClick: handleSpreadsheetUpload,
							disabled: spreadsheetCount === 0,
							icon: <CloudUpload />,
						}
					)}
					{renderSection(
						"Screenshots",
						"Upload one or more images for Pay By Link — extracted data is shown for review before saving",
						<ImageOutlined fontSize="small" color="primary" />,
						imageTypes,
						{
							label: "Process images",
							onClick: handlePayByLinkProcess,
							disabled: payByLinkImages.length === 0,
							icon: <ImageOutlined />,
						}
					)}
				</Box>

				<Box
					sx={{
						flexShrink: 0,
						px: { xs: 2.5, md: 3 },
						py: 2.5,
						borderTop: "1px solid",
						borderColor: "divider",
						bgcolor: "grey.50",
						display: "flex",
						flexDirection: { xs: "column", sm: "row" },
						alignItems: { xs: "stretch", sm: "center" },
						justifyContent: "flex-end",
						gap: 2,
					}}>
					<Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
						<Button
							variant="text"
							onClick={() => {
								setUploadLogsTab(LOG_TABS.SPREADSHEET);
								setFileDialogOpen(true);
							}}
							startIcon={<TableChartOutlined />}
							sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
							Spreadsheet upload logs
						</Button>
						<Button
							variant="text"
							onClick={() => {
								setUploadLogsTab(LOG_TABS.PAY_BY_LINK);
								setFileDialogOpen(true);
							}}
							startIcon={<ImageOutlined />}
							sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
							Pay By Link upload logs
						</Button>
						<Button
							variant="outlined"
							onClick={handleReset}
							disabled={selectedCount === 0}
							sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3 }}>
							Reset all
						</Button>
					</Box>
				</Box>
			</Paper>

			<FileDetailsDialogue
				open={fileDialogOpen}
				onClose={() => setFileDialogOpen(false)}
				initialTab={uploadLogsTab}
			/>
			<PayByLinkPreviewDialog
				open={payByLinkPreviewOpen}
				onClose={() => {
					if (payByLinkApproving) return;
					setPayByLinkPreviewOpen(false);
					setPayByLinkPreview(null);
					setPayByLinkPendingImages([]);
				}}
				previewData={payByLinkPreview}
				images={payByLinkPendingImages}
				onApprove={handlePayByLinkApprove}
				approving={payByLinkApproving}
			/>
		</Box>
	);
};

export default FileUpload;
